import {
  Skeleton,
  SkeletonText,
  SkeletonCircle,
  SkeletonButton,
  SkeletonImage,
  SkeletonCard,
  SkeletonTitle,
  SkeletonSubtitle,
  SkeletonParagraph,
} from "@/app/components/ui/skeleton";

// Homepage Skeleton
export function HomePageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8 space-y-12 min-h-screen">
      {/* Hero Slider Skeleton */}
      <div className="relative h-[400px] rounded-lg overflow-hidden">
        <Skeleton className="h-full w-full" />
        <div className="absolute inset-0 flex items-center justify-between px-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-4 text-center">
            <SkeletonTitle className="mx-auto" />
            <SkeletonSubtitle className="mx-auto" />
            <SkeletonButton className="w-32 mx-auto" />
          </div>
          <Skeleton className="h-10 w-10 rounded-full" />
        </div>
      </div>

      {/* Categories Section */}
      <div className="space-y-6">
        <SkeletonTitle />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <SkeletonCard />
              <SkeletonText className="w-3/4 mx-auto" />
            </div>
          ))}
        </div>
      </div>

      {/* News Section */}
      <div className="space-y-6">
        <SkeletonTitle />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <SkeletonImage />
              <SkeletonTitle className="w-3/4" />
              <SkeletonParagraph lines={2} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Login Page Skeleton
export function LoginPageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-12 flex justify-center min-h-screen">
      <div className="w-full max-w-md space-y-6 bg-white p-8 rounded-lg shadow-md">
        <SkeletonTitle className="mx-auto" />
        <div className="space-y-4">
          <div className="space-y-2">
            <SkeletonText className="w-24" />
            <SkeletonText className="h-10" />
          </div>
          <div className="space-y-2">
            <SkeletonText className="w-24" />
            <SkeletonText className="h-10" />
          </div>
          <SkeletonText className="w-40" />
          <SkeletonButton />
        </div>
      </div>
    </div>
  );
}

// Register Page Skeleton
export function RegisterPageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-12 flex justify-center min-h-screen">
      <div className="w-full max-w-md space-y-6 bg-white p-8 rounded-lg shadow-md">
        <SkeletonTitle className="mx-auto" />
        <div className="space-y-4">
          <div className="space-y-2">
            <SkeletonText className="w-24" />
            <SkeletonText className="h-10" />
          </div>
          <div className="space-y-2">
            <SkeletonText className="w-24" />
            <SkeletonText className="h-10" />
          </div>
          <div className="space-y-2">
            <SkeletonText className="w-24" />
            <SkeletonText className="h-10" />
          </div>
          <div className="space-y-2">
            <SkeletonText className="w-24" />
            <SkeletonText className="h-10" />
          </div>
          <SkeletonButton />
        </div>
      </div>
    </div>
  );
}

// Albums Page Skeleton
export function AlbumsPageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8 space-y-8 min-h-screen">
      <SkeletonTitle />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="space-y-3">
            <SkeletonCard />
            <SkeletonText className="w-3/4 mx-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Album Category Page Skeleton
export function AlbumCategoryPageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8 space-y-8 min-h-screen">
      <div className="flex items-center space-x-4">
        <SkeletonTitle />
        <SkeletonText className="w-20" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="space-y-3">
            <SkeletonImage />
            <SkeletonText className="w-3/4" />
            <SkeletonText className="w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

// News Page Skeleton
export function NewsPageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8 space-y-8 min-h-screen">
      <SkeletonTitle />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-4 bg-white p-4 rounded-lg shadow-sm">
            <SkeletonImage />
            <SkeletonTitle />
            <SkeletonText className="w-1/3" />
            <SkeletonParagraph lines={3} />
          </div>
        ))}
      </div>
    </div>
  );
}

// About Page Skeleton
export function AboutPageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8 space-y-8 min-h-screen">
      <SkeletonTitle />
      <div className="flex flex-col md:flex-row gap-8">
        <div className="w-full md:w-1/2">
          <SkeletonImage className="h-[300px]" />
        </div>
        <div className="w-full md:w-1/2 space-y-4">
          <SkeletonTitle />
          <SkeletonParagraph lines={6} />
        </div>
      </div>

      <div className="space-y-6 mt-12">
        <SkeletonTitle />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="space-y-3 p-4 bg-white rounded-lg shadow-sm"
            >
              <SkeletonCircle className="mx-auto" />
              <SkeletonTitle className="mx-auto w-1/2" />
              <SkeletonParagraph lines={3} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Forgot Password Page Skeleton
export function ForgotPasswordPageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-12 flex justify-center min-h-screen">
      <div className="w-full max-w-md space-y-6 bg-white p-8 rounded-lg shadow-md">
        <SkeletonTitle className="mx-auto" />
        <SkeletonParagraph lines={2} />
        <div className="space-y-4">
          <div className="space-y-2">
            <SkeletonText className="w-24" />
            <SkeletonText className="h-10" />
          </div>
          <SkeletonButton />
        </div>
      </div>
    </div>
  );
}

// Contact Page Skeleton (already implemented in the page)
export function ContactPageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-12 min-h-screen">
      <div className="relative overflow-hidden bg-white border border-gray-200 rounded-lg shadow-lg">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gray-400 via-orange-400 to-gray-400"></div>

        <div className="flex flex-col md:flex-row">
          {/* Contact Form Skeleton */}
          <div className="w-full md:w-1/2 p-8 md:p-10">
            <SkeletonTitle className="mb-6" />

            <div className="space-y-5">
              <SkeletonText className="h-12" />
              <SkeletonText className="h-12" />
              <SkeletonText className="h-12" />
              <SkeletonText className="h-32" />
              <SkeletonButton />
            </div>
          </div>

          {/* Map and Contact Info Skeleton */}
          <div className="w-full md:w-1/2 bg-gray-100 p-8 md:p-10 flex flex-col justify-between">
            <Skeleton className="h-48 rounded-lg mb-8" />

            <div className="space-y-6">
              <div className="flex items-start">
                <SkeletonCircle className="mr-4" />
                <div className="flex-1">
                  <SkeletonSubtitle className="mb-2" />
                  <SkeletonText />
                </div>
              </div>

              <div className="flex items-start">
                <SkeletonCircle className="mr-4" />
                <div className="flex-1">
                  <SkeletonSubtitle className="mb-2" />
                  <SkeletonText className="w-32" />
                </div>
              </div>

              <div className="flex items-start">
                <SkeletonCircle className="mr-4" />
                <div className="flex-1">
                  <SkeletonSubtitle className="mb-2" />
                  <SkeletonText className="w-40" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Google Map Skeleton */}
      <Skeleton className="mt-12 h-[400px] rounded-lg" />
    </div>
  );
}

// Order Page Skeleton
export function OrderPageSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8 space-y-8 min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="animate-pulse">
            <div className="h-8 w-1/3 bg-gray-200 rounded mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="h-6 w-1/4 bg-gray-200 rounded mb-4"></div>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-20 bg-gray-200 rounded"></div>
                  ))}
                </div>
                <div className="h-6 w-1/4 bg-gray-200 rounded mb-4"></div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-12 bg-gray-200 rounded"></div>
                  ))}
                </div>
                <div className="h-6 w-1/4 bg-gray-200 rounded mb-4"></div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-12 bg-gray-200 rounded"></div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-1">
                <div className="h-6 w-2/3 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-2 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex justify-between">
                      <div className="h-4 w-1/3 bg-gray-200 rounded"></div>
                      <div className="h-4 w-1/6 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
                <div className="h-12 bg-gray-200 rounded mt-6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BelgelerPageSkeleton() {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-12 animate-pulse">
        <div className="h-10 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
        <div className="h-5 bg-gray-200 rounded w-2/3 mx-auto"></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="border rounded-lg overflow-hidden animate-pulse"
          >
            <div className="p-6">
              <div className="h-6 bg-gray-200 rounded w-2/3 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-5 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
